/* ==========================================================================
   CK Price Slider  (BugHerd #50 — collection price filter needs a range slider)

   A dual-thumb range slider that drives the existing Dawn price <input>s.
   It's a custom element so it re-initialises automatically whenever the facet
   form re-renders the filters over AJAX. The two range inputs carry NO `name`
   attribute, so they're never serialised — they only mirror their values into
   the named `filter.v.price.gte` / `.lte` text inputs, which are what actually
   submit. Writing to those inputs + dispatching a bubbling `input` event lets
   Dawn's FacetFiltersForm pick the change up through its debounced submit.
   ========================================================================== */
class CkPriceSlider extends HTMLElement {
  connectedCallback() {
    // The slider sits as a sibling *before* <price-range> (not inside it) so
    // Dawn's PriceRange never attaches its clamp logic to our range inputs.
    const priceRange =
      (this.parentElement && this.parentElement.querySelector('price-range')) ||
      (this.nextElementSibling && this.nextElementSibling.matches('price-range') ? this.nextElementSibling : null);
    if (!priceRange) return;

    const textInputs = priceRange.querySelectorAll('.ck-collection__price-input');
    this.minText = textInputs[0];
    this.maxText = textInputs[1];
    this.minRange = this.querySelector('[data-slider-min]');
    this.maxRange = this.querySelector('[data-slider-max]');
    this.fill = this.querySelector('[data-slider-fill]');
    if (!this.minText || !this.maxText || !this.minRange || !this.maxRange || !this.fill) return;

    this.floor = Number(this.minText.getAttribute('data-min')) || 0;
    this.ceil = Number(this.maxText.getAttribute('data-max')) || 0;
    if (this.ceil <= this.floor) {
      // Every product shares one price — a slider would be meaningless.
      this.setAttribute('hidden', '');
      return;
    }

    [this.minRange, this.maxRange].forEach((r) => {
      r.min = this.floor;
      r.max = this.ceil;
      r.step = '1';
    });

    // Seed thumbs from whatever the text inputs currently hold (reflects the URL
    // filter state after an AJAX re-render); empty = full range.
    this.minRange.value = this.parseNum(this.minText.value, this.floor);
    this.maxRange.value = this.parseNum(this.maxText.value, this.ceil);
    this.clampThumbs();
    this.paint();

    this.minRange.addEventListener('input', () => this.onSlide('min'));
    this.maxRange.addEventListener('input', () => this.onSlide('max'));

    // Keep the slider in sync when someone types straight into the fields.
    this.onTextInput = () => this.syncFromText();
    this.minText.addEventListener('input', this.onTextInput);
    this.maxText.addEventListener('input', this.onTextInput);
  }

  parseNum(raw, fallback) {
    if (raw == null || String(raw).trim() === '') return fallback;
    const n = Number(String(raw).replace(/[^0-9.]/g, ''));
    if (!isFinite(n)) return fallback;
    return Math.min(this.ceil, Math.max(this.floor, n));
  }

  // Prevent the two thumbs from crossing over each other.
  clampThumbs() {
    let lo = Number(this.minRange.value);
    let hi = Number(this.maxRange.value);
    if (lo > hi) {
      lo = hi;
      this.minRange.value = lo;
    }
  }

  onSlide(which) {
    let lo = Number(this.minRange.value);
    let hi = Number(this.maxRange.value);
    if (lo > hi) {
      if (which === 'min') { lo = hi; this.minRange.value = lo; }
      else { hi = lo; this.maxRange.value = hi; }
    }
    // Leave a bound blank when it sits at the extreme, matching how Dawn treats
    // an empty field (no lower / no upper limit) so we don't submit noise.
    this.minText.value = lo > this.floor ? String(lo) : '';
    this.maxText.value = hi < this.ceil ? String(hi) : '';
    this.paint();
    // Nudge the facet form to re-filter (debounced in FacetFiltersForm).
    this.minText.dispatchEvent(new Event('input', { bubbles: true }));
  }

  syncFromText() {
    this.minRange.value = this.parseNum(this.minText.value, this.floor);
    this.maxRange.value = this.parseNum(this.maxText.value, this.ceil);
    this.clampThumbs();
    this.paint();
  }

  paint() {
    const span = this.ceil - this.floor;
    const lo = Number(this.minRange.value);
    const hi = Number(this.maxRange.value);
    this.fill.style.left = ((lo - this.floor) / span) * 100 + '%';
    this.fill.style.right = (100 - ((hi - this.floor) / span) * 100) + '%';
  }
}

customElements.define('ck-price-slider', CkPriceSlider);
